import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import {
  siteName,
  siteSlogan,
  siteIntro,
  stats,
  plotTimeline,
  keyNodes,
  chapters,
  characters,
  topics,
  faqs,
} from "@/mocks/mangaData";

const SUBMIT_ADDR = "https://readdy.ai/api/form/da2jkjiamq5jpe1hru8g";

function NewsletterForm() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honey = (data.get("phone_alt") as string) || "";
    if (honey.trim()) {
      setStatus("success");
      setMessage("订阅成功，感谢关注甜蜜惩罚漫画剧情追踪站！");
      form.reset();
      return;
    }
    const payload = new FormData();
    const email = data.get("email") as string;
    if (email) payload.append("email", email);

    try {
      const res = await fetch(SUBMIT_ADDR, { method: "POST", body: payload });
      const text = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string } } | null = null;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || "";
      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        setMessage("订阅成功，新分话更新后我们会第一时间通知你！");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "订阅失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络开小差了，请稍后重试。");
    }
  };

  return (
    <form
      data-readdy-form="剧情更新订阅表单"
      onSubmit={handleSubmit}
      className="mx-auto mt-8 max-w-xl w-full"
    >
      <div className="flex flex-col sm:flex-row gap-2.5">
        <label className="flex-1">
          <span className="sr-only">邮箱地址</span>
          <input
            type="email"
            name="email"
            required
            placeholder="输入你的邮箱地址"
            className="w-full rounded-full bg-background-50/95 border border-background-300 px-5 py-3 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </label>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-1.5 rounded-full bg-primary-500 text-background-50 px-6 py-3 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          订阅更新
          <i className="ri-send-plane-line text-base leading-none"></i>
        </button>
      </div>
      <div className="hp-field" aria-hidden="true">
        <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" readOnly />
      </div>
      {status === "success" && (
        <p className="mt-3 text-sm text-primary-700 bg-primary-100/70 rounded-lg px-4 py-2.5">
          <i className="ri-checkbox-circle-fill mr-1.5"></i>
          {message}
        </p>
      )}
      {status === "error" && (
        <p className="mt-3 text-sm text-secondary-900 bg-secondary-100 rounded-lg px-4 py-2.5">
          <i className="ri-error-warning-fill mr-1.5"></i>
          {message}
        </p>
      )}
    </form>
  );
}

export default function Home() {
  const previewTimeline = plotTimeline.slice(0, 3);
  const previewChapters = chapters.slice(0, 3);
  const previewCharacters = characters.slice(0, 4);

  return (
    <Layout>
      {/* Hero */}
      <section className="relative flex min-h-[560px] md:min-h-[640px] items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Dreamy%20romantic%20ink-wash%20illustration%20of%20a%20misty%20rose%20garden%20at%20dusk%20with%20falling%20cherry%20blossom%20petals%20and%20soft%20golden%20light%2C%20subtle%20silhouettes%20of%20two%20people%20near%20a%20pavilion%20in%20the%20distance%2C%20deep%20rose%20red%20and%20warm%20amber%20palette%2C%20painterly%20texture%2C%20ethereal%20cinematic%20atmosphere%2C%20elegant%20minimal%20wide%20composition%2C%20high%20detail&width=1600&height=900&seq=hero-main&orientation=landscape"
          alt="甜蜜惩罚漫画剧情追踪站首屏背景插画"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/40"></div>
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 md:px-6 py-16 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/15 backdrop-blur border border-background-50/30 px-4 py-1.5 text-xs md:text-sm font-medium text-background-50">
            <i className="ri-heart-3-fill text-accent-400"></i>
            读者向剧情追踪站 · 结局线索持续更新
          </span>
          <h1 className="mt-6 font-heading text-3xl md:text-5xl lg:text-6xl font-bold leading-tight text-background-50">
            {siteName}
          </h1>
          <p className="mt-4 font-heading text-xl md:text-2xl text-accent-200">{siteSlogan}</p>
          <p className="mx-auto mt-6 max-w-2xl text-sm md:text-base leading-7 text-background-100/90">
            {siteIntro}
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="#plot"
              className="inline-flex items-center gap-2 rounded-full bg-primary-500 text-background-50 px-7 py-3 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              查看剧情主线
              <i className="ri-arrow-right-line text-base leading-none"></i>
            </a>
            <Link
              to="/chapters"
              className="inline-flex items-center gap-2 rounded-full bg-background-50/15 backdrop-blur border border-background-50/40 px-7 py-3 text-sm font-medium text-background-50 hover:bg-background-50/25 transition-colors whitespace-nowrap"
            >
              最新分话解析
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-background-200 bg-background-100/60">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 grid grid-cols-2 gap-6 md:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-heading text-3xl md:text-4xl font-bold text-primary-600">{s.value}</p>
              <p className="mt-1 text-xs md:text-sm text-foreground-600">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 主线速览 */}
      <section id="plot" className="py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-9">
            <div>
              <span className="font-label text-xs md:text-sm font-semibold text-primary-600 uppercase tracking-wider">
                剧情主线
              </span>
              <h2 className="mt-2 font-heading text-2xl md:text-4xl font-bold">主线脉络 · 分卷梳理</h2>
              <p className="mt-3 max-w-2xl text-sm md:text-base leading-7 text-foreground-700">
                从初遇到终章，把《甜蜜惩罚漫画》的主线走向拆成分卷脉络，让你一眼看清剧情从何而来、将往何处。
              </p>
            </div>
            <Link
              to="/plot"
              className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-primary-700 hover:text-primary-800 whitespace-nowrap"
            >
              查看完整主线
              <i className="ri-arrow-right-line text-base leading-none"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {previewTimeline.map((node, i) => (
              <Link
                key={node.phase}
                to="/plot"
                className="group rounded-lg bg-background-100 border border-background-200 p-6 hover:border-primary-300 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="grid w-10 h-10 place-items-center rounded-full bg-primary-500 text-background-50 font-heading text-sm font-bold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="rounded-full bg-accent-100 text-accent-900 px-3 py-1 text-xs font-medium">
                    {node.range}
                  </span>
                </div>
                <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-950 group-hover:text-primary-700 transition-colors">
                  {node.title}
                </h3>
                <p className="mt-2.5 text-sm leading-6 text-foreground-700">{node.summary}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {node.tags.map((t) => (
                    <span key={t} className="text-xs text-foreground-500 bg-background-200/70 rounded px-2 py-0.5">
                      {t}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-5">
            {keyNodes.slice(0, 3).map((n) => (
              <Link
                key={n.chapter}
                to="/chapters"
                className="group flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5 hover:border-primary-300 transition-colors"
              >
                <span className="font-heading text-primary-600 text-sm font-bold mt-0.5 whitespace-nowrap">
                  {n.chapter}
                </span>
                <div>
                  <h4 className="font-medium text-foreground-950 group-hover:text-primary-700 transition-colors">
                    {n.title}
                  </h4>
                  <p className="mt-1.5 text-xs leading-5 text-foreground-600">{n.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 最新分话 */}
      <section className="py-14 md:py-20 bg-background-100/60 border-y border-background-200">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-9">
            <div>
              <span className="font-label text-xs md:text-sm font-semibold text-primary-600 uppercase tracking-wider">
                分话解析
              </span>
              <h2 className="mt-2 font-heading text-2xl md:text-4xl font-bold">最新分话 · 单话详解</h2>
              <p className="mt-3 max-w-2xl text-sm md:text-base leading-7 text-foreground-700">
                追踪最新更新，逐话拆解关键节点、人物反应与伏笔回收信号，追更剧情不再错过任何细节。
              </p>
            </div>
            <Link
              to="/chapters"
              className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-primary-700 hover:text-primary-800 whitespace-nowrap"
            >
              全部分话
              <i className="ri-arrow-right-line text-base leading-none"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {previewChapters.map((ch) => (
              <Link
                key={ch.id}
                to={`/chapters/${ch.id}`}
                className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
              >
                <div className="relative h-44 md:h-48 overflow-hidden">
                  <img
                    src={ch.image}
                    alt={`${ch.num} ${ch.title} 分话插画`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-foreground-950/70 backdrop-blur text-background-50 px-3 py-1 text-xs font-medium">
                    {ch.num}
                  </span>
                  <span className="absolute right-3 top-3 rounded-full bg-accent-500 text-background-50 px-3 py-1 text-xs font-medium">
                    {ch.tag}
                  </span>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-timer-line"></i>
                    {ch.date}
                  </div>
                  <h3 className="mt-2 font-heading text-lg font-semibold text-foreground-950 group-hover:text-primary-700 transition-colors">
                    {ch.title}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-700 line-clamp-2">{ch.summary}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 热门角色 */}
      <section className="py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-9">
            <div>
              <span className="font-label text-xs md:text-sm font-semibold text-primary-600 uppercase tracking-wider">
                角色解析
              </span>
              <h2 className="mt-2 font-heading text-2xl md:text-4xl font-bold">热门角色 · 人物命运</h2>
              <p className="mt-3 max-w-2xl text-sm md:text-base leading-7 text-foreground-700">
                盘点关键角色的成长弧光、关系网络与结局线索，看懂人物，也就看懂了大半个剧情走向。
              </p>
            </div>
            <Link
              to="/characters"
              className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-primary-700 hover:text-primary-800 whitespace-nowrap"
            >
              全部角色
              <i className="ri-arrow-right-line text-base leading-none"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {previewCharacters.map((c) => (
              <Link
                key={c.id}
                to="/characters"
                className="group overflow-hidden rounded-lg border border-background-200 bg-background-100 hover:border-primary-300 transition-colors"
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={c.image}
                    alt={`甜蜜惩罚漫画角色 ${c.name} ${c.role}`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-primary-500 text-background-50 px-3 py-1 text-xs font-medium">
                    {c.role}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-lg font-semibold text-foreground-950">{c.name}</h3>
                  <p className="mt-1 text-xs text-foreground-500">{c.alias}</p>
                  <p className="mt-3 text-sm leading-6 text-foreground-700 line-clamp-3">{c.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 剧情时间线 */}
      <section className="py-14 md:py-20 bg-background-100/60 border-y border-background-200">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="max-w-2xl mb-10">
            <span className="font-label text-xs md:text-sm font-semibold text-primary-600 uppercase tracking-wider">
              关键节点
            </span>
            <h2 className="mt-2 font-heading text-2xl md:text-4xl font-bold">追更时间轴 · 别错过这些分话</h2>
            <p className="mt-3 text-sm md:text-base leading-7 text-foreground-700">
              把全篇最重要、读者讨论度最高的分话节点按顺序排出来，新读者按这个顺序读，剧情脉络最清晰。
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-4 top-2 bottom-2 w-px bg-primary-200 md:left-1/2"></div>
            <div className="flex flex-col gap-8">
              {keyNodes.map((node, i) => (
                <div
                  key={node.chapter}
                  className={`relative flex flex-col gap-3 pl-12 md:pl-0 md:w-1/2 ${
                    i % 2 === 0
                      ? "md:pr-12 md:text-right"
                      : "md:ml-auto md:pl-12"
                  }`}
                >
                  <span
                    className={`absolute left-2.5 md:left-auto grid h-4 w-4 place-items-center ${
                      i % 2 === 0 ? "md:right-[-8px]" : "md:left-[-8px]"
                    } top-1 rounded-full border-2 border-background-50 bg-accent-500`}
                  ></span>
                  <span
                    className={`inline-flex items-center gap-2 text-xs font-semibold text-accent-900 ${
                      i % 2 === 0 ? "md:justify-end" : ""
                    }`}
                  >
                    <span className="rounded-full bg-accent-100 px-3 py-1">{node.chapter}</span>
                    <span className="rounded-full bg-secondary-100 text-secondary-900 px-3 py-1">
                      {node.type}
                    </span>
                  </span>
                  <h3 className="font-heading text-lg font-semibold text-foreground-950">{node.title}</h3>
                  <p className="text-sm leading-6 text-foreground-700">{node.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 专题推荐 */}
      <section className="py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="max-w-2xl mb-9">
            <span className="font-label text-xs md:text-sm font-semibold text-primary-600 uppercase tracking-wider">
              专题追踪
            </span>
            <h2 className="mt-2 font-heading text-2xl md:text-4xl font-bold">深度专题 · 一站式读懂</h2>
            <p className="mt-3 text-sm md:text-base leading-7 text-foreground-700">
              把分散在多话的信息整理成专题，无论是身世疑云、感情弧光还是伏笔回收，一次看个明白。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {topics.map((topic) => (
              <Link
                key={topic.title}
                to={topic.path}
                className="group relative overflow-hidden rounded-lg border border-background-200 bg-foreground-950 p-7 min-h-[220px] flex flex-col justify-end hover:border-primary-400 transition-colors"
              >
                <div className="absolute inset-0 opacity-20">
                  <img
                    src={
                      topic.tag === "主线专题"
                        ? "https://readdy.ai/api/search-image?query=Elegant%20vintage%20manuscript%20pages%20with%20faded%20rose%20stamps%20and%20golden%20wax%20seal%20on%20a%20warm%20dark%20wood%20desk%2C%20dreamy%20ink-wash%20editorial%20illustration%2C%20deep%20rose%20and%20muted%20gold%20tones%2C%20moody%20warm%20lighting%2C%20minimal%20cinematic%20composition%2C%20high%20detail&width=700&height=700&seq=topic-plot&orientation=squarish"
                        : topic.tag === "角色专题"
                          ? "https://readdy.ai/api/search-image?query=Delicate%20ink%20portraits%20and%20handwritten%20notes%20with%20rose%20petal%20shadows%20arranged%20on%20a%20soft%20cream%20background%2C%20dreamy%20romantic%20ink-wash%20editorial%20illustration%2C%20soft%20rose%20and%20peach%20tones%2C%20gentle%20warm%20lighting%2C%20minimal%20elegant%20composition%2C%20high%20detail&width=700&height=700&seq=topic-char&orientation=squarish"
                          : "https://readdy.ai/api/search-image?query=A%20vintage%20pocket%20watch%20with%20a%20winding%20timeline%20of%20golden%20threads%20and%20small%20rose%20accents%20on%20a%20warm%20dark%20surface%2C%20dreamy%20ink-wash%20editorial%20illustration%2C%20deep%20rose%20and%20warm%20amber%20palette%2C%20moody%20cinematic%20lighting%2C%20minimal%20composition%2C%20high%20detail&width=700&height=700&seq=topic-chapter&orientation=squarish"
                    }
                    alt={`${topic.title} 专题配图`}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/40 to-transparent"></div>
                <div className="relative z-10">
                  <span className="rounded-full bg-accent-500 text-background-50 px-3 py-1 text-xs font-medium">
                    {topic.tag}
                  </span>
                  <h3 className="mt-3 font-heading text-xl font-semibold text-background-50 group-hover:text-accent-200 transition-colors">
                    {topic.title}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-background-100/80">{topic.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-accent-300">
                    前往阅读
                    <i className="ri-arrow-right-line text-base leading-none"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 常见问题 */}
      <section className="py-14 md:py-20 bg-background-100/60 border-t border-background-200">
        <div className="mx-auto max-w-4xl px-4 md:px-6">
          <div className="text-center mb-10">
            <span className="font-label text-xs md:text-sm font-semibold text-primary-600 uppercase tracking-wider">
              常见问题
            </span>
            <h2 className="mt-2 font-heading text-2xl md:text-4xl font-bold">读者高频问题</h2>
            <p className="mt-3 text-sm md:text-base leading-7 text-foreground-700">
              关于更新进度、主线脉络与结局线索的常见疑问，这里一次说清楚。
            </p>
          </div>

          <div className="flex flex-col gap-3">
            {faqs.slice(0, 4).map((f) => (
              <details
                key={f.q}
                className="group rounded-lg border border-background-200 bg-background-50 px-5 py-4 open:border-primary-300"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-4 list-none">
                  <h3 className="font-medium text-foreground-950">{f.q}</h3>
                  <i className="ri-add-line text-xl text-primary-600 shrink-0 group-open:rotate-45 transition-transform"></i>
                </summary>
                <p className="mt-3 text-sm leading-7 text-foreground-700">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link to="/faq" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-800">
              查看全部常见问题
              <i className="ri-arrow-right-line text-base leading-none"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* 订阅 CTA */}
      <section className="py-14 md:py-20 bg-primary-700">
        <div className="mx-auto max-w-4xl px-4 md:px-6 text-center">
          <span className="grid w-14 h-14 mx-auto place-items-center rounded-full bg-primary-500 text-background-50 text-2xl">
            <i className="ri-heart-3-fill"></i>
          </span>
          <h2 className="mt-5 font-heading text-2xl md:text-4xl font-bold text-background-50">
            订阅剧情追踪，追更不再迷路
          </h2>
          <p className="mt-3 text-sm md:text-base leading-7 text-background-100/85">
            留下邮箱，最新分话更新与结局线索盘点我们会第一时间通知你。
          </p>
          <NewsletterForm />
        </div>
      </section>
    </Layout>
  );
}